import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDownTrayIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { dataTableButtonClasses, type DataTableMode, type SortOrder } from '@odo/shared-ui';
import FilterDropdown from '../../../components/filtering/FilterDropdown';
import { useBillingInvoices } from '../../../hooks/useBilling';
import { useViewportPageSize } from '../../../hooks/useViewportPageSize';
import type { BillingInvoiceListItem } from '../../../services/billing';
import { STATUS_LABELS, STATUS_PRIORITY } from '../shared/statusLabels';
import { ExportInvoicesModal } from './ExportInvoicesModal';
import { InvoiceDrawer } from './InvoiceDrawer';
import { InvoicesTable, type InvoicesSortBy } from './InvoicesTable';

// The open invoice drawer lives in the URL (`?invoice=<id>`), same pattern as
// PatientOverview's `?tab=`: deep-linked/refreshed loads reopen it directly.
const INVOICE_QUERY_PARAM = 'invoice';

// Same convention as PatientTable.tsx/Messages.tsx (localStorage, page-scoped
// key); default 'infinite' since a practice can run to ~1,769 invoices.
const VIEW_MODE_KEY = 'invoicesTableViewMode';

// Chunk revealed per scroll/"Load more" step in infinite mode - matches
// shared-ui DataTable's own infinite-mode default chunk size.
const INFINITE_CHUNK_SIZE = 50;

function readStoredMode(): DataTableMode {
    return localStorage.getItem(VIEW_MODE_KEY) === 'paged' ? 'paged' : 'infinite';
}

// Status filter dropdown order, most-actionable first. The old status-card
// row was removed for a simpler page; this dropdown is now the only filter.
const STATUS_FILTER_OPTIONS = [
    STATUS_LABELS.overdue,
    STATUS_LABELS.open,
    STATUS_LABELS.partial,
    STATUS_LABELS.upcoming,
    STATUS_LABELS.paid,
    STATUS_LABELS.void
];

function matchesSearch(invoice: BillingInvoiceListItem, query: string): boolean {
    const haystack = [
        invoice.invoiceNumber,
        invoice.charge.patientName,
        invoice.charge.description,
        invoice.charge.productSummary
    ]
        .filter((v): v is string => !!v)
        .join(' ')
        .toLowerCase();
    return haystack.includes(query.toLowerCase());
}

function dueDateAsc(a: BillingInvoiceListItem, b: BillingInvoiceListItem): number {
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
}

// Status sorts by logical severity (STATUS_PRIORITY), not alphabetically,
// with a due-date-ascending tiebreak regardless of the column's own direction.
function compareInvoices(
    a: BillingInvoiceListItem,
    b: BillingInvoiceListItem,
    sortBy: InvoicesSortBy,
    sortOrder: SortOrder
): number {
    const dir = sortOrder === 'asc' ? 1 : -1;
    if (sortBy === 'amount') {
        return (Number(a.amount) - Number(b.amount)) * dir;
    }
    if (sortBy === 'created') {
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
    }
    if (sortBy === 'status') {
        const diff = STATUS_PRIORITY[a.derivedStatus] - STATUS_PRIORITY[b.derivedStatus];
        return diff !== 0 ? diff * dir : dueDateAsc(a, b);
    }
    if (sortBy === 'patient') {
        const nameA = a.charge.patientName;
        const nameB = b.charge.patientName;
        // No-patient rows render a dash placeholder, so pin them to the end
        // regardless of direction so a Z→A sort doesn't surface dashes on top.
        if (nameA === null || nameB === null) {
            if (nameA === null && nameB === null) return dueDateAsc(a, b);
            return nameA === null ? 1 : -1;
        }
        const diff = nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        return diff !== 0 ? diff * dir : dueDateAsc(a, b);
    }
    return dueDateAsc(a, b) * dir;
}

export const InvoicesSection: React.FC = () => {
    const { data: invoices, isLoading } = useBillingInvoices();
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [patientFilter, setPatientFilter] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    // Default sort: unpaid/overdue first, matching the mockup and the Status
    // column's own default ('asc') direction, so load and click land the same.
    const [sortBy, setSortBy] = useState<InvoicesSortBy>('status');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [page, setPage] = useState(1);
    const [mode, setMode] = useState<DataTableMode>(readStoredMode);
    const [revealedCount, setRevealedCount] = useState(INFINITE_CHUNK_SIZE);
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const openInvoiceId = searchParams.get(INVOICE_QUERY_PARAM);

    // Opening pushes a history entry (own back/forward stop); closing always
    // replaces so dismissing the drawer never leaves a stack entry Back would reopen.
    const handleOpenInvoice = useCallback(
        (id: string) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set(INVOICE_QUERY_PARAM, id);
                return next;
            });
        },
        [setSearchParams]
    );

    const handleCloseInvoice = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(INVOICE_QUERY_PARAM);
                return next;
            },
            { replace: true }
        );
    }, [setSearchParams]);

    const viewport = useViewportPageSize();
    const { pageSize } = viewport;

    const allInvoices = useMemo(() => invoices ?? [], [invoices]);

    const patientOptions = useMemo(() => {
        const names = new Set<string>();
        for (const invoice of allInvoices) {
            if (invoice.charge.patientName) names.add(invoice.charge.patientName);
        }
        return Array.from(names).sort();
    }, [allInvoices]);

    const handleSort = (column: InvoicesSortBy) => {
        if (column === sortBy) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            // Soonest due first, most urgent status first, biggest amount
            // first, patient names A→Z first.
            setSortOrder(column === 'amount' ? 'desc' : 'asc');
        }
        setPage(1);
        setRevealedCount(INFINITE_CHUNK_SIZE);
    };

    const handleModeChange = useCallback((next: DataTableMode) => {
        setMode(next);
        localStorage.setItem(VIEW_MODE_KEY, next);
        setPage(1);
        setRevealedCount(INFINITE_CHUNK_SIZE);
    }, []);

    const filteredInvoices = useMemo(() => {
        let result = allInvoices;
        if (statusFilter.length > 0) {
            result = result.filter((invoice) =>
                statusFilter.includes(STATUS_LABELS[invoice.derivedStatus])
            );
        }
        if (patientFilter.length > 0) {
            result = result.filter(
                (invoice) =>
                    invoice.charge.patientName && patientFilter.includes(invoice.charge.patientName)
            );
        }
        const query = search.trim();
        if (query) {
            result = result.filter((invoice) => matchesSearch(invoice, query));
        }
        return result;
    }, [allInvoices, statusFilter, patientFilter, search]);

    const sortedInvoices = useMemo(() => {
        return [...filteredInvoices].sort((a, b) => compareInvoices(a, b, sortBy, sortOrder));
    }, [filteredInvoices, sortBy, sortOrder]);

    useEffect(() => {
        setPage(1);
        setRevealedCount(INFINITE_CHUNK_SIZE);
    }, [statusFilter, patientFilter, search]);

    useEffect(() => {
        setPage(1);
    }, [pageSize]);

    const totalPages = Math.max(1, Math.ceil(sortedInvoices.length / pageSize));
    const paginatedInvoices = useMemo(
        () => sortedInvoices.slice((page - 1) * pageSize, page * pageSize),
        [sortedInvoices, page, pageSize]
    );

    // Infinite mode windows the loaded array instead of mounting it all,
    // unlike PatientTable.tsx's 'infinite', which hands DataTable the full list.
    const revealedInvoices = useMemo(
        () => sortedInvoices.slice(0, revealedCount),
        [sortedInvoices, revealedCount]
    );
    const hasMoreInvoices = revealedCount < sortedInvoices.length;
    const handleLoadMoreInvoices = useCallback(() => {
        setRevealedCount((prev) => Math.min(prev + INFINITE_CHUNK_SIZE, sortedInvoices.length));
    }, [sortedInvoices.length]);

    const displayedInvoices = mode === 'paged' ? paginatedInvoices : revealedInvoices;

    // Re-measure once real rows render (the loading row's height differs).
    // remeasureOnce no-ops after the first call.
    const { remeasureOnce } = viewport;
    useLayoutEffect(() => {
        if (displayedInvoices.length === 0) return;
        remeasureOnce();
    }, [displayedInvoices.length, remeasureOnce]);

    return (
        <div className="flex flex-col h-full min-h-0 gap-4">
            <div className="flex items-center justify-between gap-4 shrink-0">
                <div className="flex items-center w-[340px] h-11 bg-white border border-gray-200 rounded-xl px-4 shrink-0">
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2 shrink-0" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search invoice # or patient"
                        className="bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400 w-full"
                    />
                </div>
                <div className="flex items-center gap-2.5">
                    <FilterDropdown
                        title="Patient"
                        options={patientOptions}
                        selectedOptions={patientFilter}
                        onChange={setPatientFilter}
                        align="right"
                    />
                    <FilterDropdown
                        title="Status"
                        options={STATUS_FILTER_OPTIONS}
                        selectedOptions={statusFilter}
                        onChange={setStatusFilter}
                        align="right"
                    />
                    <button
                        type="button"
                        onClick={() => setIsExportOpen(true)}
                        className={dataTableButtonClasses()}
                    >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        Export
                    </button>
                </div>
            </div>

            <InvoicesTable
                invoices={displayedInvoices}
                isLoading={isLoading}
                sort={{ by: sortBy, order: sortOrder, onSort: handleSort }}
                onOpenInvoice={handleOpenInvoice}
                pagination={{
                    page,
                    totalPages,
                    total: sortedInvoices.length,
                    onPage: (p) => setPage(Math.min(Math.max(1, p), totalPages))
                }}
                mode={mode}
                onModeChange={handleModeChange}
                onLoadMore={handleLoadMoreInvoices}
                hasMore={hasMoreInvoices}
                refs={{
                    card: viewport.tableCardRef,
                    head: viewport.tableHeaderRef,
                    body: viewport.tableBodyRef,
                    pagination: viewport.paginationRef
                }}
            />

            {openInvoiceId && (
                <InvoiceDrawer
                    invoiceId={openInvoiceId}
                    onClose={handleCloseInvoice}
                    closeDisabled={isExportOpen}
                />
            )}

            {isExportOpen && <ExportInvoicesModal onClose={() => setIsExportOpen(false)} />}
        </div>
    );
};
